import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useAppSelector } from '../redux/hooks';
import { useLazyDetailedGraph } from './useLazyDetailedGraph';
import {
  processData,
  getSingleBranchExpansion,
  getExpansionToSeeType,
  getVisibleNodes,
  calculateExpansionForType,
  getDescendants,
  isAncillaryType,
} from '../components/graph-viz/utils/graphUtils';
import type { GraphNode, GraphLink } from '../components/graph-viz/types';
import { ComponentColors } from '../components/graph-viz/types';
import {
  buildEntityLookup,
  mapEntitiesToNodes,
  mapConnectionsToEdges,
  hasConnectionMismatch,
  isDataFromCorrectSite,
  buildPVSiteData
} from '../components/graph-viz/utils/dataTransforms';

export const useNetworkViewer = () => {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const {
    entities, connections, isLoadingEntities, isLoadingConnections,
    expandNode, expandedNodeIds: apiExpandedNodeIds, expandingNodeId,
    lastExpansionResult, clearLastExpansionResult,
  } = useLazyDetailedGraph(true);

  // ── Selection state ──────────────────────────────────────────────────
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);

  // ── Graph viewer state ───────────────────────────────────────────────
  const [orientation, setOrientation] = useState<'TD' | 'LR'>('TD');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [powerVisMode, setPowerVisMode] = useState<'none' | 'shadow' | 'halo' | 'bar'>('none');
  const [highlightedPath, setHighlightedPath] = useState<Set<string>>(new Set());
  const clickPhaseRef = useRef<{ nodeId: string; phase: number }>({ nodeId: '', phase: 0 });
  const [nodeClickPhase, setNodeClickPhase] = useState(0);
  const [shouldSwitchToDetails, setShouldSwitchToDetails] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showAuxiliary, setShowAuxiliary] = useState(true);

  // ── Deferred processing state ────────────────────────────────────────
  const [graphData, setGraphData] = useState<{ nodes: GraphNode[]; links: any[] }>({ nodes: [], links: [] });
  const [isProcessing, setIsProcessing] = useState(false);
  const processedDataKeyRef = useRef<string>('');
  const initializedForRef = useRef<string>('');
  const prevSiteIdRef = useRef<string | undefined>(undefined);

  // ── Reset all state when site changes ────────────────────────────────
  useEffect(() => {
    const currentSiteId = currentSite?.site_id;
    if (prevSiteIdRef.current !== undefined && prevSiteIdRef.current !== currentSiteId) {
      setGraphData({ nodes: [], links: [] });
      setExpandedIds(new Set());
      setSelectedComponents([]);
      setHighlightedPath(new Set());
      processedDataKeyRef.current = '';
      initializedForRef.current = '';
    }
    prevSiteIdRef.current = currentSiteId;
  }, [currentSite?.site_id]);

  // ── Stable fingerprint for raw data to detect changes ────────────────
  const rawDataKey = useMemo(() => {
    if (!entities.length) return '';
    const firstEntity = entities[0];
    const firstId = firstEntity?.entity_id || (firstEntity as any)?.id || '';
    const firstConn = connections[0];
    const connKey = firstConn ? `${firstConn.source_entity_id}-${firstConn.target_entity_id}` : '';
    return `${firstId}-${entities.length}-${connections.length}-${connKey}`;
  }, [entities, connections]);

  // ── Deferred data processing ─────────────────────────────────────────
  useEffect(() => {
    if (!rawDataKey || rawDataKey === processedDataKeyRef.current) return;

    // Skip if we just handled an incremental expansion — the graphData
    // already has the new nodes/links appended.
    if (processedDataKeyRef.current.startsWith('__incremental_')) {
      processedDataKeyRef.current = rawDataKey;
      return;
    }

    if (isLoadingEntities || isLoadingConnections) return;

    // Validate connection integrity
    const entityIds = new Set(entities.map(e => e.entity_id || (e as any).id));
    if (hasConnectionMismatch(connections, entityIds)) {
      processedDataKeyRef.current = '';
      setIsProcessing(false);
      return;
    }

    // Verify data belongs to current site
    if (!isDataFromCorrectSite(entities, currentSite?.site_id)) {
      return;
    }

    setIsProcessing(true);

    const frameId = requestAnimationFrame(() => {
      // Build lookup map and transform data
      const entityById = buildEntityLookup(entities);
      const nodes = mapEntitiesToNodes(entities);
      const { edges, missingSource, missingTarget } = mapConnectionsToEdges(connections, entityById);

      // Guard against stale connections
      const totalMissing = missingSource + missingTarget;
      const totalEndpoints = connections.length * 2;
      if (connections.length > 0 && totalEndpoints > 0 && totalMissing / totalEndpoints > 0.5) {
        console.warn(`[NetworkViewerTab] Connection mismatch: ${totalMissing}/${totalEndpoints} endpoints missing.`);
        processedDataKeyRef.current = '';
        setIsProcessing(false);
        return;
      }

      const pvSiteData = buildPVSiteData(nodes, edges);
      const processed = processData(pvSiteData);

      processedDataKeyRef.current = rawDataKey;
      setGraphData(processed);
      setIsProcessing(false);
    });

    return () => cancelAnimationFrame(frameId);
  }, [rawDataKey, entities, connections, isLoadingEntities, isLoadingConnections, currentSite]);

  // ── Incremental expansion: bypass processData for expand results ────
  useEffect(() => {
    if (!lastExpansionResult) return;

    const { parentId, children, edges } = lastExpansionResult;

    // Find parent node in current graphData to determine base tier
    const parentNode = graphData.nodes.find(n => n.id === parentId);
    const parentTier = parentNode?.tier ?? 0;

    // Build a set of existing node IDs to avoid duplicates
    const existingNodeIds = new Set(graphData.nodes.map(n => n.id));

    // Build parent lookup from edges: source_id -> target_id (child -> parent)
    const childToParent = new Map<string, string>();
    edges.forEach(edge => {
      childToParent.set(edge.source_id, edge.target_id);
    });

    // Map children to GraphNode objects
    const newNodes: GraphNode[] = [];
    children.forEach(child => {
      if (existingNodeIds.has(child.primary_id)) return;

      const type = (child.component_type || 'unknown').toLowerCase().replace(/ /g, '_');
      const relativeTier = child.tier ?? 1;
      const absoluteTier = parentTier + relativeTier;
      const edgeParent = childToParent.get(child.primary_id);
      const nodeParentId = edgeParent || parentId;

      newNodes.push({
        id: child.primary_id,
        label: type === 'pv_module' ? '' : child.primary_id,
        type,
        color: ComponentColors[type] || ComponentColors.default,
        group: type,
        details: {
          component_type: type,
          "Primary ID": child.primary_id,
          Aliases: child.aliases || [],
          component_order: child.component_order,
          properties: child.properties,
          configuration: child.configuration,
          pref: child.pref,
          api_child_count: child.child_count,
        },
        val: 10,
        tier: absoluteTier,
        siblingIndex: 0,
        groupIndex: 0,
        parentId: nodeParentId,
        isAncillary: isAncillaryType(type),
        apiChildCount: child.child_count,
      });
    });

    // Map edges to GraphLink objects
    const existingLinkKeys = new Set(
      graphData.links.map((l: any) => `${typeof l.source === 'string' ? l.source : l.source}-${typeof l.target === 'string' ? l.target : l.target}`)
    );
    const newLinks: GraphLink[] = [];
    edges.forEach(edge => {
      const key = `${edge.source_id}-${edge.target_id}`;
      if (existingLinkKeys.has(key)) return;

      const signal = edge.signal || 'DC';
      let color = '#6B7280';
      if (signal === 'AC') color = '#EF4444';
      if (signal === 'DC') color = '#10B981';
      if (signal === 'COMM') color = '#3B82F6';

      newLinks.push({
        source: edge.source_id,
        target: edge.target_id,
        label: edge.relation || 'connects',
        color,
        details: {
          source: edge.source_id,
          target: edge.target_id,
          relation: edge.relation || 'connects',
          signal,
          properties: edge.properties,
          configuration: edge.configuration,
        },
      });
    });

    if (newNodes.length > 0 || newLinks.length > 0) {
      // Assign sibling indices for new nodes
      const siblingGroups = new Map<string, GraphNode[]>();
      newNodes.forEach(node => {
        const key = `${node.parentId || 'root'}_${node.type}`;
        if (!siblingGroups.has(key)) siblingGroups.set(key, []);
        siblingGroups.get(key)!.push(node);
      });
      siblingGroups.forEach(group => {
        group.sort((a, b) => a.id.localeCompare(b.id));
        group.forEach((node, i) => {
          node.siblingIndex = i;
          node.groupIndex = i;
        });
      });

      setGraphData(prev => ({
        nodes: [...prev.nodes, ...newNodes],
        links: [...prev.links, ...newLinks],
      }));
    }

    processedDataKeyRef.current = `__incremental_${Date.now()}`;
    clearLastExpansionResult();
  }, [lastExpansionResult, graphData.nodes, graphData.links, clearLastExpansionResult]);

  // ── Site data key for initialization tracking ────────────────────────
  const siteDataKey = useMemo(() => {
    if (graphData.nodes.length === 0) return '';
    const rootNode = graphData.nodes.find(n => n.tier === 0);
    return rootNode?.id || '';
  }, [graphData.nodes]);

  // ── Initialize expansion state when new site data loads ──────────────
  useEffect(() => {
    if (isLoadingEntities || isLoadingConnections || !siteDataKey || siteDataKey === initializedForRef.current) {
      return;
    }
    let initial = getExpansionToSeeType(graphData.nodes, graphData.links, 'inverter');
    if (initial.size === 0) {
      initial = getSingleBranchExpansion(graphData.nodes, graphData.links);
    }
    setExpandedIds(initial);
    setHighlightedPath(new Set());
    setSelectedComponents([]);
    initializedForRef.current = siteDataKey;
  }, [isLoadingEntities, isLoadingConnections, siteDataKey, graphData.nodes, graphData.links]);

  // ── Visible nodes based on expansion state ───────────────────────────
  const visibleNodeIds = useMemo(() => {
    return getVisibleNodes(graphData.nodes, graphData.links, expandedIds, showAuxiliary);
  }, [graphData.nodes, graphData.links, expandedIds, showAuxiliary, siteDataKey, isLoadingEntities, isLoadingConnections]);

  const selectedNodeIds = new Set(selectedComponents);

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleNodeClick = useCallback((node: GraphNode) => {
    // Always select the node and switch to details tab
    setSelectedComponents([node.id]);
    setShouldSwitchToDetails(true);
    setHighlightedPath(new Set());
    clickPhaseRef.current = { nodeId: node.id, phase: 1 };
    setNodeClickPhase(1);

    // Single-click toggle: expand or collapse
    const isExpanded = expandedIds.has(node.id);
    if (isExpanded) {
      // Collapse this node
      setExpandedIds(prev => {
        const next = new Set(prev);
        next.delete(node.id);
        return next;
      });
    } else {
      // Expand: call expand-detailed API if node has children
      const apiChildCount = node.apiChildCount ?? 0;
      if (apiChildCount > 0 && !apiExpandedNodeIds.has(node.id)) {
        expandNode(node.id);
      }

      setExpandedIds(prev => {
        const next = new Set(prev);
        next.add(node.id);
        return next;
      });
    }

    // Navigate as-built viewer to the node's page reference
    const pref = node.details?.pref;
    if (pref != null) {
      const page = typeof pref === 'number' ? pref : parseInt(String(pref), 10);
      if (!isNaN(page)) {
        window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', {
          detail: {
            nodeId: node.id,
            nodeLabel: node.label || node.id,
            page: page,
            pageRefs: [{ label: node.label || node.id, page: page }]
          }
        }));
      }
    }
  }, [expandedIds, setSelectedComponents, graphData.links, apiExpandedNodeIds, expandNode]);

  const handleToggleExpand = useCallback((nodeId: string) => {
    const isExpanded = expandedIds.has(nodeId);
    if (isExpanded) {
      setExpandedIds(prev => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
      return;
    }

    // Call expand-detailed API if node has children
    const node = graphData.nodes.find(n => n.id === nodeId);
    const apiChildCount = node?.apiChildCount ?? 0;
    if (apiChildCount > 0 && !apiExpandedNodeIds.has(nodeId)) {
      expandNode(nodeId);
    }

    setExpandedIds(prev => {
      const next = new Set(prev);
      next.add(nodeId);
      return next;
    });
  }, [expandedIds, graphData.nodes, apiExpandedNodeIds, expandNode]);

  const handleBackgroundClick = useCallback(() => {
    setSelectedComponents([]);
    setHighlightedPath(new Set());
    clickPhaseRef.current = { nodeId: '', phase: 0 };
    setNodeClickPhase(0);
  }, [setSelectedComponents]);

  const handleMultiNodeSelect = useCallback((nodeIds: string[]) => {
    setSelectedComponents(nodeIds);
    setHighlightedPath(new Set());
    setShouldSwitchToDetails(false);

    const newExpanded = new Set<string>();
    nodeIds.forEach(nodeId => {
      const node = graphData.nodes.find(n => n.id === nodeId);
      if (node) {
        let curr: GraphNode | undefined = node.parentId ? graphData.nodes.find(n => n.id === node.parentId) : undefined;
        while (curr) {
          newExpanded.add(curr.id);
          const parentId = curr.parentId;
          curr = parentId ? graphData.nodes.find(n => n.id === parentId) : undefined;
        }
      }
    });
    setExpandedIds(newExpanded);
  }, [setSelectedComponents, graphData.nodes]);

  const handleSearchNodeSelect = useCallback((nodeId: string) => {
    const node = graphData.nodes.find(n => n.id === nodeId);
    if (!node) return;

    const newExpanded = new Set(expandedIds);
    let curr: GraphNode | undefined = node.parentId ? graphData.nodes.find(n => n.id === node.parentId) : undefined;
    while (curr) {
      newExpanded.add(curr.id);
      const parentId = curr.parentId;
      curr = parentId ? graphData.nodes.find(n => n.id === parentId) : undefined;
    }
    setExpandedIds(newExpanded);

    setSelectedComponents([nodeId]);
    setShouldSwitchToDetails(true);
    setHighlightedPath(new Set());
    clickPhaseRef.current = { nodeId, phase: 1 };
    setNodeClickPhase(1);
  }, [graphData.nodes, expandedIds, setSelectedComponents]);

  const handleLegendClick = useCallback((type: string) => {
    const expansion = getExpansionToSeeType(graphData.nodes, graphData.links, type);
    setExpandedIds(expansion);
    setSelectedComponents([]);
    setHighlightedPath(new Set());
  }, [graphData.nodes, graphData.links, setSelectedComponents]);

  const handleToggleType = useCallback((sourceNodeIds: string[], type: string) => {
    const newExpanded = calculateExpansionForType(
      graphData.nodes, graphData.links, expandedIds, sourceNodeIds, type
    );
    setExpandedIds(newExpanded);
  }, [graphData.nodes, graphData.links, expandedIds]);

  const handleCollapseTypeAndBelow = useCallback((sourceNodeIds: string[], type: string) => {
    const descendants = new Set<string>();
    sourceNodeIds.forEach(sourceId => {
      const { nodeIds } = getDescendants(sourceId, graphData.links);
      nodeIds.forEach(id => descendants.add(id));
    });

    const parentsToCollapse = new Set<string>();
    graphData.nodes.forEach(node => {
      if (descendants.has(node.id) && node.type === type && node.parentId) {
        parentsToCollapse.add(node.parentId);
      }
    });

    const newExpanded = new Set<string>();
    expandedIds.forEach(id => {
      if (!parentsToCollapse.has(id)) {
        newExpanded.add(id);
      }
    });
    setExpandedIds(newExpanded);
  }, [graphData.nodes, graphData.links, expandedIds]);

  const handleExpandAuxiliary = useCallback(() => setShowAuxiliary(true), []);
  const handleCollapseAuxiliary = useCallback(() => setShowAuxiliary(false), []);

  const handlePowerSelect = useCallback((filter: { tier: number; power: number } | null) => {
    if (filter) {
      const matching = graphData.nodes.filter(n =>
        n.tier === filter.tier && Math.abs((n.cumulativePower || 0) - filter.power) < 0.1
      );
      setSelectedComponents(matching.map(n => n.id));
      setHighlightedPath(new Set());
    }
  }, [graphData.nodes]);

  const handlePropertySelect = useCallback((key: string, value: any) => {
    const matching = graphData.nodes.filter(n =>
      (n.details.properties && n.details.properties[key] === value) ||
      (n.details.configuration && n.details.configuration[key] === value)
    );
    setSelectedComponents(matching.map(n => n.id));
    setHighlightedPath(new Set());
  }, [graphData.nodes]);

  const handleDetailsPanelClose = useCallback(() => {
    setSelectedComponents([]);
    setHighlightedPath(new Set());
  }, []);

  // ── External navigation (Events → Network) ─────────────────────────
  const pendingNavigationRef = useRef<string[] | null>(null);

  const navigateToEntities = useCallback((entityIdentifiers: string[]) => {
    const matchingNodeIds: string[] = [];

    for (const identifier of entityIdentifiers) {
      const normalizedId = identifier.toLowerCase().trim();
      const matchingNode = graphData.nodes.find(n => {
        const nodeLabel = (n.label || '').toLowerCase().trim();
        const nodeId = (n.id || '').toLowerCase().trim();
        return nodeLabel === normalizedId || nodeId === normalizedId;
      });
      if (matchingNode) {
        matchingNodeIds.push(matchingNode.id);
      }
    }

    if (matchingNodeIds.length === 0) {
      console.warn('[NetworkViewer] No matching nodes found for:', entityIdentifiers);
      return;
    }

    if (matchingNodeIds.length === 1) {
      handleSearchNodeSelect(matchingNodeIds[0]);
    } else {
      handleMultiNodeSelect(matchingNodeIds);
    }
  }, [graphData.nodes, handleSearchNodeSelect, handleMultiNodeSelect]);

  // Listen for navigateToNetworkNode events from other tabs
  useEffect(() => {
    const handler = (e: Event) => {
      const { entityIdentifiers, siteId } = (e as CustomEvent).detail || {};
      if (!entityIdentifiers || entityIdentifiers.length === 0) return;

      // If graph data not loaded yet, queue for later
      if (graphData.nodes.length === 0) {
        pendingNavigationRef.current = entityIdentifiers;
        return;
      }

      navigateToEntities(entityIdentifiers);
    };

    window.addEventListener('navigateToNetworkNode', handler);
    return () => window.removeEventListener('navigateToNetworkNode', handler);
  }, [graphData.nodes.length, navigateToEntities]);

  // Process pending navigation when graph data becomes available
  useEffect(() => {
    if (pendingNavigationRef.current && graphData.nodes.length > 0) {
      const identifiers = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      requestAnimationFrame(() => navigateToEntities(identifiers));
    }
  }, [graphData.nodes.length, navigateToEntities]);

  // ── Computed values ──────────────────────────────────────────────────
  const selectedNodes = graphData.nodes.filter(n => selectedNodeIds.has(n.id));
  const isExpansionReady = siteDataKey === '' || siteDataKey === initializedForRef.current;
  const isLoading = isLoadingEntities || isLoadingConnections;

  return {
    // Data
    currentSite,
    graphData,
    visibleNodeIds,
    selectedNodes,
    selectedNodeIds,

    // State
    orientation,
    expandedIds,
    powerVisMode,
    highlightedPath,
    isSidebarOpen,
    nodeClickPhase,
    shouldSwitchToDetails,

    // Status
    isLoading,
    isProcessing,
    isExpansionReady,
    expandingNodeId,

    // State setters (for direct use in JSX)
    setOrientation,
    setPowerVisMode,
    setIsSidebarOpen,

    // Handlers
    handleNodeClick,
    handleToggleExpand,
    handleBackgroundClick,
    handleMultiNodeSelect,
    handleSearchNodeSelect,
    handleLegendClick,
    handleToggleType,
    handleCollapseTypeAndBelow,
    handleExpandAuxiliary,
    handleCollapseAuxiliary,
    handlePowerSelect,
    handlePropertySelect,
    handleDetailsPanelClose,
  };
};
